import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabaseClient.js';
import Grid from '@mui/material/Grid2';
import { Container, IconButton, Typography, TextField, Stack, Button } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InputAdornment from '@mui/material/InputAdornment';
import { useDispatch } from 'react-redux';
import { setLoginStatus, setUserDetails, setCurrentOrg, setAllOrgs, setRole } from '../../store.js';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../../components/BrandLogo';
import useAlert from '../../alert/useAlert.js';
import PopupMenu from '../PopupMenu/PopupMenu.js';
import Loader from '../../loader.js';
import { useLocation } from 'react-router-dom';

const SignupScreen = () => {
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [signupDetailsSubmitted, setSignupDetailsSubmitted] = useState(false);
  const [tosCheckbox, setTosCheckbox] = useState(false);
  const [privacyPolicyCheckbox, setPrivacyPolicyCheckbox] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentScreen, setCurrentScreen] = useState('signup');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const { setAlert } = useAlert();
  const hasMounted = useRef(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch(); 


  useEffect(() => {
    if (hasMounted.current) { return; }
    dispatch(setCurrentOrg(null));
    dispatch(setAllOrgs(null));
    dispatch(setRole(null));
    dispatch(setLoginStatus(false));
    dispatch(setUserDetails(null));
    hasMounted.current = true;
  }, []);

  const handleSignUp = async () => {
    if(!newPassword || !confirmPassword || !email || passwordError || confirmPasswordError) {
      setAlert('Please enter all the required fields properly', 'error');
      return;
    }
    if(!tosCheckbox || !privacyPolicyCheckbox) {
      setAlert('Please agree to the Terms & Conditions and Privacy Policy', 'warning');
      return;
    }
    if(newPassword !== confirmPassword) {
      setAlert('Passwords do not match', 'error');
      return;
    }
    setLoading(true);

    // Supabase Auth turns a database-trigger rejection into a generic error.
    // Ask the narrow preflight first so a recently deleted account receives a
    // clear message; the auth.users trigger remains the final enforcement.
    const { data: registrationHoldActive, error: registrationHoldError } = await supabase
      .rpc('is_account_deletion_registration_hold_active', { p_email: email });
    if (registrationHoldError) {
      console.error('Error checking registration hold:', registrationHoldError);
      setAlert('Unable to verify this email. Please try again later.', 'error');
      setLoading(false);
      return;
    }
    if (registrationHoldActive) {
      setAlert('This email was recently deleted. Please try again in a few minutes.', 'error');
      setLoading(false);
      return;
    }

    try {
      if (location.state?.userMetadata) {
        const user = await updateUser();
        if (user) navigate('/onboarding');
        return;
      }
      const userExists = await checkEmailExists(email);
      if (userExists) {
        setAlert('User with this email already exists. Please login', 'error');
        return;
      }
      // Supabase manages existing/unconfirmed identities. Never delete and
      // recreate an Auth user to retry signup.
      await signUpUser();
    } catch (error) {
      console.error('Error creating user:', error);
      setAlert('Unable to create an account. Please try again later', 'error');
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async () => {
    const { data, error } = await supabase.auth.updateUser({
      email: email,
      password: newPassword,
      data: { userData: location.state?.userMetadata || null },
    });
    if (error) {
      setAlert(error.message, 'error');
      return null;
    } else {
      return data.user;
    }
  }

  const signUpUser = async () => {
    const { error } = await supabase.auth.signUp({
      email: email,
      password: newPassword,
      options: {
        // Same-origin, not env-baked: a build-time URL sends the verified
        // session to a different origin than the one the user signed up on
        // (dev/uat/prod share builds of this env var), stranding the signup.
        emailRedirectTo: `${window.location.origin}/emailVerify`,
        data: {
          userData: location.state && location.state.userMetadata ? location.state.userMetadata : null,
        },
      },
    });
    if (error) {
      setAlert(error.message, 'error');
    } else {
      setCurrentScreen('verificationLink');
    }
  }

  const checkEmailExists = async (email) => {
    let { data, error } = await supabase.from('user_accounts').select('*');
    if (error) {
      console.error("Error: ", error);
      return null;
    } else {
      const decryptedData = await Promise.all(data.map(async (user) => {
        const decryptedEmail = await supabase.rpc('decrypt_text', { encrypted_data: user.email });
        if (decryptedEmail.error) {
          console.error("Error: ", decryptedEmail.error);
          return null;
        }
        return { ...user, email: decryptedEmail.data };
      }));
      return decryptedData.find(user => user.email === email);
    }
  }

  const validatePassword = (password) => {
    // Regex for at least one letter, one special character, and minimum 6 characters
    const strongPasswordRegex = /^(?=.*[A-Za-z])(?=.*[^A-Za-z0-9]).{6,}$/;
    if (!strongPasswordRegex.test(password)) {
      setPasswordError('Password must contain at least 6 characters, one letter, and one special character.');
    } else {
      setPasswordError('');
    }
  };

  const validateConfirmPassword = (password) => {
    // Regex for at least one letter, one special character, and minimum 6 characters
    const strongPasswordRegex = /^(?=.*[A-Za-z])(?=.*[^A-Za-z0-9]).{6,}$/;
    if (!strongPasswordRegex.test(password)) {
      setConfirmPasswordError('Password must contain at least 6 characters, one letter, and one special character.');
    } else {
      setConfirmPasswordError('');
    }
  };

  return (
    <Container disableGutters maxWidth={false}> 
      <Grid container spacing={2} sx={{ minHeight: '100vh', minWidth: '100vw', marginTop: -3 }}>
      <Grid size={12} sx={{ backgroundColor: '#B9F4F2', height: '90px', padding: '15px 10px 0px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
        <BrandLogo onClick={() => { navigate('/'); }} style={{cursor: 'pointer'}}/>
        </Grid>
        {
            currentScreen === 'signup' ?
            <Grid size={12} sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'center'}}>
            <Stack spacing={3} width={{xs: '80vw', md: '40vw'}} direction="column" alignItems="stretch" justifyContent="center" style={{backgroundColor: '#f5f5f5', padding: '30px', borderRadius: '10px', boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)' }}>
                <Typography variant="h6" sx={{fontWeight: 'bold', fontSize: '30px'}}>Sign Up</Typography>
                <Typography variant="body1"> Already have an account? <span onClick={() => { navigate('/') }} style={{ cursor: 'pointer', color: '#29C0BB' }}>Log in</span> </Typography>
                <TextField
                  required
                  fullWidth
                  label="Email"
                  variant="outlined"
                  focused
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email" 
                  sx={{ marginTop: '20px', marginBottom: '16px', backgroundColor: '#f5f5f5' }} 
                  error={signupDetailsSubmitted && !email}
                  helperText={signupDetailsSubmitted && !email && "Email is required"}
                />
                <TextField
                  required
                  fullWidth
                  label="Create a password"
                  variant="outlined"
                  focused
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); validatePassword(e.target.value); }}
                  placeholder="Create a password" 
                  type={showPassword ? "text" : "password"}
                  sx={{ marginTop: '20px', marginBottom: '16px', backgroundColor: '#f5f5f5' }} 
                  error={(signupDetailsSubmitted && !newPassword) || passwordError}
                  helperText={((signupDetailsSubmitted && !newPassword) || passwordError) ? passwordError ?? "Enter a password" : ""}
                  slotProps={{
                    input: {
                      endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => {setShowPassword(!showPassword); }}
                          edge="end"
                        >
                          {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                      ),
                    }
                  }}
                />
                <TextField
                  required
                  fullWidth
                  label="Confirm password"
                  variant="outlined"
                  focused
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); validateConfirmPassword(e.target.value); }}
                  placeholder="Confirm password" 
                  type={showConfirmPassword ? "text" : "password"}
                  sx={{ marginTop: '20px', marginBottom: '16px', backgroundColor: '#f5f5f5' }} 
                  error={(signupDetailsSubmitted && !confirmPassword) || confirmPasswordError}
                  helperText={((signupDetailsSubmitted && !confirmPassword) || confirmPasswordError) ? confirmPasswordError ?? "Enter a password" : ""}
                  slotProps={{
                    input: {
                      endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                      ),
                    }
                  }}
                />
                <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="center">
                    <input type="checkbox" checked={tosCheckbox} onChange={() => setTosCheckbox(!tosCheckbox)} />
                    <Typography variant="body1">I agree to the <span style={{ cursor: 'pointer', color: '#29C0BB', textDecoration: 'underline' }}><a href="https://tikcare.co/terms-of-service/" target="_blank" rel="noreferrer">Terms & Conditions</a></span> </Typography>
                </Stack>
                <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="center">
                    <input type="checkbox" checked={privacyPolicyCheckbox} onChange={() => setPrivacyPolicyCheckbox(!privacyPolicyCheckbox)} />
                    <Typography variant="body1">I agree to the <span style={{ cursor: 'pointer', color: '#29C0BB', textDecoration: 'underline' }}><a href="https://tikcare.co/privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</a></span> </Typography>
                </Stack>
                <Button onClick={() => { setSignupDetailsSubmitted(true); handleSignUp(); }} variant="outlined" sx={{width: '100%', height: '50px', borderRadius: 30, backgroundColor: '#29C0BB', border: 0}}>
                  <Typography variant="button" sx={{color: '#FFFFFF', fontSize: '15px'}}>Sign Up</Typography>
                </Button>
              </Stack>
            </Grid>
            :
            currentScreen === 'verificationLink' ?
            <Grid size={12} sx={{padding:'50px', display: 'flex', alignItems: 'flex-start', justifyContent: 'center'}}>
                <Stack spacing={3} width={{xs: '80vw', md: '40vw'}} direction="column" alignItems="flex-start" justifyContent="center" style={{backgroundColor: '#f5f5f5', padding: '30px', borderRadius: '10px', boxShadow: '0 0 10px rgba(0, 0, 0, 0.2)' }}>
                <Typography variant="h6" sx={{fontWeight: 'bold', fontSize: '30px'}}>A verification link has been sent to your email. Please verify your email to login.</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    If you don't see the email in your inbox, please check your spam or junk folder.
                </Typography>
                <Typography variant="body1">Remember your password? <span onClick={() => navigate('/')} style={{ cursor: 'pointer', color: '#29C0BB' }}>Login</span> </Typography>
                </Stack>
            </Grid>  
            : null      
        }
        {
        loading && (
          <PopupMenu>
            <Stack direction="column" alignItems="center" justifyContent="center" spacing={2}>
              <Typography variant="h6">Creating Account</Typography>
              <Loader />
            </Stack>
          </PopupMenu> 
          ) 
        }
      </Grid>
    </Container>
  );
};

export default SignupScreen;

